/**
 * @deprecated 兼容桥接层 — 将新 authStore 状态同步到旧 LoginStore
 *
 * 使用方式：在 AuthOrchestrator mount 后调用 startLoginStoreBridge()
 * 用途：确保仍消费 LoginStore.isLoggedIn 的旧代码得到正确值
 * 生命周期：随 SDK 完全移除后一并删除
 */
import { subscribe } from "valtio";
import { authStore } from "../store/auth-store";
import { appOspSecurity } from "@features/api/osp";

let unsubscribe: (() => void) | null = null;
let idTokenFetchInFlight = false;

/**
 * Whether authStore has ever left its initial `idle` value (i.e. the
 * orchestrator actually started restoring/authenticating a session, or
 * already reached a resolved state). The initial `idle` is NOT a
 * "no session" verdict — treating it as one wipes the login state that
 * initFromCache() restored synchronously during module evaluation, which
 * makes useRouteProtection hard-loads (refresh / deep link on a protected
 * route) bounce a logged-in user back to HOME before restore completes.
 */
let authStoreSessionResolved = false;

function fetchAndSyncIdToken(): void {
  if (idTokenFetchInFlight) {
    return;
  }
  idTokenFetchInFlight = true;
  appOspSecurity
    .idToken()
    .then((result) => {
      if (result.id_token) {
        try {
          const { LoginStore } = require("@/src/store/modules/login");
          if (LoginStore.auth) {
            LoginStore.auth = { ...LoginStore.auth, idToken: result.id_token };
          }
        } catch {
          /* ignore */
        }
      }
    })
    .catch(() => {
      /* idToken fetch failed — quest APIs will degrade */
    })
    .finally(() => {
      idTokenFetchInFlight = false;
    });
}

export function startLoginStoreBridge(): () => void {
  if (unsubscribe) {
    return unsubscribe;
  }

  const syncState = () => {
    try {
      const { LoginStore } = require("@/src/store/modules/login");
      const status = authStore.status;

      if (status.state !== "idle" || authStoreSessionResolved) {
        authStoreSessionResolved = true;
      }

      if (status.state === "authenticated") {
        if (!LoginStore.isLoggedIn) {
          LoginStore.isLoggedIn = true;
          LoginStore.isFetching = false;
          LoginStore.isLoading = false;
        }
        if (
          status.tokens.accessToken &&
          LoginStore.auth?.accessToken !== status.tokens.accessToken
        ) {
          LoginStore.auth = {
            accessToken: status.tokens.accessToken,
            refreshToken: status.tokens.refreshToken,
            idToken: LoginStore.auth?.idToken,
          };
        }
        if (!LoginStore.auth?.idToken) {
          fetchAndSyncIdToken();
        }
      } else if (status.state === "idle") {
        if (!authStoreSessionResolved) {
          // Initial idle before the orchestrator started restore: keep the
          // synchronously restored login state; the first real transition
          // (restoring → authenticated/idle) will re-sync with a verdict.
          //
          // `isLoading` 是例外，必须在这里也清掉：它的语义是"认证流程进行中"，
          // 初值写死 true，而无 token 的冷启动里 coldStartRestore() 根本不 mutate
          // authStore（cold-start.ts 无 token 分支直接 return）→ subscribe 永不触发
          // → 本函数不会再被调用第二次，isLoading 就永久停在 true。
          // 后果：`/login` 的 Apple/Google 按钮永久 disabled（SocialLoginButtons
          // `disabled={mergedLoading}`）、LoginLandingPage 永久 <Spin/>。
          // 清它不影响本分支要保护的 isLoggedIn / auth / eoaAddress。
          LoginStore.isLoading = false;
          return;
        }
        if (LoginStore.isLoggedIn) {
          LoginStore.isLoggedIn = false;
        }
        LoginStore.isFetching = false;
        LoginStore.isLoading = false;
        // idle 即"无会话"：残留 auth / eoaAddress 会让旧代码读到已失效的 token
        LoginStore.auth = undefined;
        LoginStore.eoaAddress = undefined;
        LoginStore.loginType = undefined;
        LoginStore.subLoginType = undefined;
      } else if (
        status.state === "restoring" ||
        status.state === "authenticating"
      ) {
        LoginStore.isFetching = true;
      }
    } catch {
      /* LoginStore not yet loaded or circular dep — safe to ignore */
    }
  };

  syncState();

  unsubscribe = subscribe(authStore, syncState);

  return () => {
    unsubscribe?.();
    unsubscribe = null;
  };
}
